/**
 * Field Sales Dashboard API layer (plain functions).
 *
 * Ported from the source repo's `FieldSalesApi` class. Every method keeps its
 * name, arguments and return shape, so `hooks/useFieldSalesDashboard.js` calls
 * it unchanged through `import * as fieldSalesApi from '../api/fieldSalesApi.js'`.
 *
 * Differences from the source class, following `SfeDashboard/api/sfeApi.js`:
 *   - Requests go through the shared `apiClient` (utils/axiosConfig) at
 *     `getApiUrl('field-sales/...')`. The interceptor attaches the Databricks
 *     token because `isDatabricksEndpoint()` matches `field-sales`. The
 *     source's middleware proxy path, its own axios instance, the token
 *     providers and the temporary X-API-Key header are not used.
 *   - Every response goes through `unwrapEnvelope`: `success: false` throws a
 *     `FieldSalesApiError` with the allow-listed backend message
 *     (utils/apiErrors) instead of silently returning the fallback shape.
 *   - Every rejection is a `FieldSalesApiError` carrying an `apiError`
 *     descriptor. Aborts are rethrown untouched.
 *
 * This file is still the SINGLE place where backend field names are mapped to
 * UI-facing names, and the only place the month YYYY-MM ↔ YYYYMM boundary is
 * crossed. Monetary values are passed through in LAKHS, untouched.
 */
import apiClient from "../../../utils/axiosConfig";
import { API_ENDPOINTS, getApiUrl } from "../../../Configurations/config";
import {
  ERROR_KIND,
  GENERIC_MESSAGES,
  normalizeApiError,
  readAllowListedMessage,
  readReferenceId,
} from "../../../utils/apiErrors";

const EP = API_ENDPOINTS.FIELD_SALES;

// ─── errors ───────────────────────────────────────────────────────────────────

/** Message used when a failed envelope carries no usable text of its own. */
const FALLBACK_FAILURE_MESSAGE = GENERIC_MESSAGES[ERROR_KIND.UNKNOWN];

/** Trimmed non-empty string, or null. */
function readString(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Plain-ish object test: excludes null and arrays. */
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * A failed Field Sales request, whether it arrived as an HTTP error or as a
 * `success: false` envelope on a 200. `message` is always display-safe;
 * `apiError` mirrors the descriptor the shared Axios pipeline attaches.
 */
export class FieldSalesApiError extends Error {
  constructor(message, { referenceId = null, kind = ERROR_KIND.UNKNOWN, status = null, cause } = {}) {
    const safeMessage = readString(message) ?? FALLBACK_FAILURE_MESSAGE;
    super(safeMessage);
    this.name = "FieldSalesApiError";
    this.referenceId =
      readString(referenceId) ??
      (typeof referenceId === "number" && Number.isFinite(referenceId) ? String(referenceId) : null);
    this.kind = kind;
    this.status = status;
    if (cause !== undefined) this.cause = cause;
    this.apiError = {
      kind,
      status,
      message: safeMessage,
      referenceId: this.referenceId,
      retryAfterMs: null,
      raw: cause === undefined ? this : cause,
    };
  }
}

/** True for a cancellation (AbortController or Axios CanceledError). */
function isAbortError(error) {
  if (!isRecord(error)) return false;
  return error.name === "AbortError" || error.name === "CanceledError" || error.code === "ERR_CANCELED";
}

/** The response body, accepting either an Axios response or the body itself. */
function readBody(response) {
  if (!isRecord(response)) return response;
  if ("success" in response) return response;
  if ("data" in response) return response.data;
  return response;
}

/**
 * Unwraps the `{ success, data, message }` envelope.
 * - `success === false` → throws `FieldSalesApiError` with the backend message.
 * - `success` present and not false → returns `data`.
 * - `success` absent → returns the body unchanged.
 *
 * @param {unknown} response an Axios response, or the body on its own
 * @returns {unknown}
 * @throws {FieldSalesApiError}
 */
export function unwrapEnvelope(response) {
  const body = readBody(response);
  if (!isRecord(body) || !("success" in body)) return body;

  if (body.success === false) {
    const asError = { response: { data: body } };
    throw new FieldSalesApiError(readAllowListedMessage(asError) ?? FALLBACK_FAILURE_MESSAGE, {
      referenceId: readReferenceId(asError),
    });
  }
  return body.data;
}

/** Normalizes any rejection into a `FieldSalesApiError` (aborts pass through). */
function asFieldSalesApiError(error) {
  if (isAbortError(error) || error instanceof FieldSalesApiError) return error;
  const descriptor = isRecord(error) && isRecord(error.apiError) ? error.apiError : normalizeApiError(error);
  return new FieldSalesApiError(descriptor.message ?? FALLBACK_FAILURE_MESSAGE, {
    referenceId: descriptor.referenceId ?? null,
    kind: descriptor.kind ?? ERROR_KIND.UNKNOWN,
    status: descriptor.status ?? null,
    cause: error,
  });
}

/** One GET (endpoint from API_ENDPOINTS.FIELD_SALES), unwrapped; `{}` when `data` is empty. */
async function envelopeGet(endpoint, params = {}, { signal } = {}) {
  try {
    const response = await apiClient.get(getApiUrl(endpoint), { params, signal });
    const data = unwrapEnvelope(response);
    return isRecord(data) ? data : {};
  } catch (error) {
    throw asFieldSalesApiError(error);
  }
}

// ─── params ───────────────────────────────────────────────────────────────────

/** Backend accepts at most this many values per list filter. */
const MAX_FILTER_VALUES = 50;

/** The only valid credit-note `type` values on the wire. */
const CREDIT_NOTE_TYPES = ["expiry", "sales_return", "rate_diff"];

/**
 * Normalise a filter value (string | string[] | null) into a trimmed,
 * de-duplicated, non-empty array capped at MAX_FILTER_VALUES.
 */
function toValueList(value) {
  const arr = Array.isArray(value) ? value : value == null ? [] : [value];
  const cleaned = arr.map((v) => (v == null ? "" : String(v).trim())).filter(Boolean);
  return [...new Set(cleaned)].slice(0, MAX_FILTER_VALUES);
}

/** Set `key=v1,v2,...` when the list has entries; empty = no filter. */
function setListParam(params, key, value) {
  const list = toValueList(value);
  if (list.length) params[key] = list.join(",");
}

/**
 * UI/URL month "YYYY-MM" → wire "YYYYMM". Any other shape passes through.
 * @param {string|null|undefined} month
 * @returns {string|null}
 */
function monthToWire(month) {
  if (!month) return null;
  const m = /^(\d{4})-(\d{2})$/.exec(String(month));
  return m ? `${m[1]}${m[2]}` : String(month);
}

/** Set `months` as a CSV of YYYYMM values; empty = backend default (latest). */
function setMonthsParam(params, months) {
  const wire = toValueList(months).map(monthToWire).filter(Boolean);
  if (wire.length) params.months = wire.join(",");
}

/**
 * The shared Field Sales params from the applied selection: fiscal_year,
 * months, division, state, brand, hq, bdm, zm. Zone, Category and DSH are NOT
 * sent (no data source yet; greyed in the filter bar).
 */
function fieldSalesParams(filters = {}) {
  const { year, months, division, state, brand, hq, bdm, zm } = filters || {};
  const params = {};
  setListParam(params, "fiscal_year", year);
  setMonthsParam(params, months);
  setListParam(params, "division", division);
  setListParam(params, "state", state);
  setListParam(params, "brand", brand);
  setListParam(params, "hq", hq);
  setListParam(params, "bdm", bdm);
  setListParam(params, "zm", zm);
  return params;
}

const asArray = (v) => (Array.isArray(v) ? v : []);
const asObject = (v) => (v && typeof v === "object" && !Array.isArray(v) ? v : {});

// ─── endpoints ────────────────────────────────────────────────────────────────

/**
 * Filter options. GET field-sales/filters
 * `months_available` is YYYYMM; the hook converts it to YYYY-MM.
 * @param {{ months?: string[]|string|null, division?: string[]|string, state?: string[]|string }} [params]
 */
export async function getFilters({ months, division, state } = {}) {
  const params = {};
  setMonthsParam(params, months);
  setListParam(params, "division", division);
  setListParam(params, "state", state);
  const res = await envelopeGet(EP.FILTERS, params);
  return {
    fiscalYear: asArray(res.fiscal_year),
    monthsAvailable: asArray(res.months_available),
    division: asArray(res.division),
    zone: asArray(res.zone),
    state: asArray(res.state),
    category: asArray(res.category),
    brand: asArray(res.brand),
    hq: asArray(res.hq),
    dsh: asArray(res.dsh),
    bdm: asArray(res.bdm),
    zm: asArray(res.zm),
  };
}

/** Top-line KPI summary. GET field-sales/summary */
export async function getSummary(filters = {}) {
  const res = await envelopeGet(EP.SUMMARY, fieldSalesParams(filters));
  return {
    fiscalYear: res.fiscal_year ?? "",
    measure: res.measure ?? "",
    months: asArray(res.months),
    card1: asObject(res.card1),
    card2: asObject(res.card2),
    card3: asObject(res.card3),
    card4: asObject(res.card4),
    insights: asObject(res.insights),
  };
}

/**
 * HQ rows for one KPI band. GET field-sales/summary/hqs?group&band
 * Exactly two selectors: `group` (growth | achievement | pcpm) and `band`.
 * @param {Object} [filters]
 * @param {{ group?: 'growth'|'achievement'|'pcpm', band?: string }} [selectors]
 */
export async function getSummaryHqs(filters = {}, { group, band } = {}) {
  const params = fieldSalesParams(filters);
  if (group) params.group = group;
  if (band) params.band = band;
  const res = await envelopeGet(EP.SUMMARY_HQS, params);
  return {
    fiscalYear: res.fiscal_year ?? "",
    measure: res.measure ?? "",
    group: res.group ?? (group ?? ""),
    band: res.band ?? (band ?? ""),
    rows: asArray(res.rows),
  };
}

/**
 * Sales breakdown by dimension. GET field-sales/breakdown?dim&page&page_size
 * The row list arrives on `data`; the UI uses `rows`.
 */
export async function getBreakdown(filters = {}, { dim = "brand", page = 1, pageSize = 50 } = {}) {
  const params = fieldSalesParams(filters);
  if (dim) params.dim = dim;
  params.page = page;
  params.page_size = pageSize;
  const res = await envelopeGet(EP.BREAKDOWN, params);
  return {
    dim: res.dim ?? dim,
    measure: res.measure ?? "",
    fiscalYear: res.fiscal_year ?? "",
    page: res.page ?? page,
    pageSize: res.page_size ?? pageSize,
    total: res.total ?? 0,
    rows: asArray(res.data),
    totalRow: asObject(res.total_row),
  };
}

/** Monthly sales trend (Apr → Mar). GET field-sales/trend */
export async function getTrend(filters = {}) {
  const res = await envelopeGet(EP.TREND, fieldSalesParams(filters));
  return {
    fiscalYear: res.fiscal_year ?? "",
    measure: res.measure ?? "",
    series: asArray(res.series),
  };
}

/**
 * HQ productivity top/bottom N by PCPM per TM. GET field-sales/hq-productivity?n
 * @param {{ n?: number }} [options] 10 | 20 | 30 | 50 | 100
 */
export async function getHqProductivity(filters = {}, { n } = {}) {
  const params = fieldSalesParams(filters);
  if (n != null) params.n = n;
  const res = await envelopeGet(EP.HQ_PRODUCTIVITY, params);
  return {
    fiscalYear: res.fiscal_year ?? "",
    measure: res.measure ?? "",
    n: res.n ?? 0,
    cards: asObject(res.cards),
    top: asArray(res.top),
    bottom: asArray(res.bottom),
  };
}

/**
 * PCPM × Growth matrix. GET field-sales/matrix?quad
 * @param {{ quad?: 'HH'|'HL'|'LH'|'LL' }} [options]
 */
export async function getMatrix(filters = {}, { quad } = {}) {
  const params = fieldSalesParams(filters);
  if (quad) params.quad = quad;
  const res = await envelopeGet(EP.MATRIX, params);
  return {
    fiscalYear: res.fiscal_year ?? "",
    measure: res.measure ?? "",
    quad: res.quad ?? (quad ?? ""),
    countsHq: asObject(res.counts_hq),
    countsTm: asObject(res.counts_tm),
    thresholds: asObject(res.thresholds),
    rows: asArray(res.rows),
  };
}

/** Critical HQ watchlist. GET field-sales/critical */
export async function getCritical(filters = {}) {
  const res = await envelopeGet(EP.CRITICAL, fieldSalesParams(filters));
  return {
    fiscalYear: res.fiscal_year ?? "",
    measure: res.measure ?? "",
    summary: asObject(res.summary),
    rows: asArray(res.rows),
  };
}

/** Volatility analysis. GET field-sales/volatility */
export async function getVolatility(filters = {}) {
  const res = await envelopeGet(EP.VOLATILITY, fieldSalesParams(filters));
  return {
    fiscalYear: res.fiscal_year ?? "",
    measure: res.measure ?? "",
    rows: asArray(res.rows),
  };
}

/**
 * Credit-notes analysis (sales leakage). GET field-sales/credit-notes?type
 * An unset/invalid `type` is omitted so the backend uses its own default.
 * @param {{ type?: 'expiry'|'sales_return'|'rate_diff' }} [options]
 */
export async function getCreditNotes(filters = {}, { type } = {}) {
  const params = fieldSalesParams(filters);
  if (type && CREDIT_NOTE_TYPES.includes(type)) params.type = type;
  const res = await envelopeGet(EP.CREDIT_NOTES, params);
  return {
    fiscalYear: res.fiscal_year ?? "",
    measure: res.measure ?? "",
    type: res.type ?? "",
    totals: asObject(res.totals),
    rows: asArray(res.rows),
  };
}
